/**
 * GroupView v7 — /group route. Tenant-wide consolidated view.
 *
 * Day 15 fix (Jun 12 — "tab clicks do nothing / only refresh"):
 *   - SafeCurrencySwitcher used a try/catch around <CurrencySwitcher />.
 *     A try/catch CANNOT catch a child's RENDER error — the element is only
 *     created inside the try; React renders it later, outside this call
 *     stack. So a throwing CurrencySwitcher escaped the guard and crashed
 *     all of GroupView (dead page → tab clicks no-op → ErrorBoundary's
 *     Retry button looked like a "refresh"). Replaced with a real class
 *     error boundary (RenderBoundary) that genuinely isolates the switcher:
 *     if it throws, only the switcher disappears, the rest of the page lives.
 *   - handleTabChange mutated the existing searchParams object before
 *     passing it back — a React Router anti-pattern. Now uses the
 *     functional updater with a fresh URLSearchParams.
 *
 * NOTE: CurrencySwitcher is still broken upstream — it now fails *silently*
 * (renders nothing) instead of taking the page down. The actual currency fix
 * lives in CurrencySwitcher.tsx.
 *
 * Group-wide intent: detail tabs are rendered WITHOUT a businessId prop, so
 * useGroupPnL/CashFlow/TrialBalance request the whole group. That only works
 * if api.ts omits X-Business-Id when activeBusinessSlug is null (Group Mode).
 *
 * URL contract:
 *   /group                  → overview tab
 *   /group?tab=pnl          → P&L tab
 *   /group?tab=cashflow     → Cash Flow tab
 *   /group?tab=trialbalance → Trial Balance tab
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  RefreshCcw,
  FileBarChart2,
  Banknote,
  Scale,
  LayoutGrid,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useBusinessStore from "@/stores/businessStore";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import type { CompareWith } from "@/hooks/useGroupReports";
import { useDisplayCurrency } from "@/context/DisplayCurrencyContext";
import GroupPerformance from "@/components/group/GroupPerformance";

import GroupTrialBalanceDetail from "@/components/reports/GroupTrialBalanceDetail";
import GroupPnLDetail from "@/components/reports/GroupPnLDetail";
import GroupCashFlowDetail from "@/components/reports/GroupCashFlowDetail";
import { CurrencySwitcher } from "@/components/group/CurrencySwitcher";

type GroupTab = "overview" | "pnl" | "cashflow" | "trialbalance";
const VALID_TABS: GroupTab[] = ["overview", "pnl", "cashflow", "trialbalance"];

function isValidTab(v: string | null): v is GroupTab {
  return v !== null && (VALID_TABS as string[]).includes(v);
}

// Local calendar dates (toISOString would give yesterday east of UTC at night).
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function getMonthToDate() {
  const now = new Date();
  return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: ymd(now) };
}

function today() {
  return ymd(new Date());
}

// ── Real render-error boundary (try/catch can't catch render errors) ──
// Isolates a single throwing child so it can't crash the whole page.
class RenderBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error("[GroupView · isolated child error]", error);
  }
  render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

const GroupView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const setActiveBusinessSlug = useBusinessStore(
    (s) => s.setActiveBusinessSlug,
  );

  // Group Mode safety net
  useEffect(() => {
    try {
      if (typeof setActiveBusinessSlug === "function") {
        setActiveBusinessSlug(null);
      }
    } catch {
      /* ignore */
    }
  }, [setActiveBusinessSlug]);

  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState<GroupTab>(
    isValidTab(urlTab) ? urlTab : "overview",
  );

  useEffect(() => {
    const next = isValidTab(urlTab) ? urlTab : "overview";
    setTab(next);
  }, [urlTab]);

  const [dateRange, setDateRange] = useState(getMonthToDate);

  const handleTabChange = (next: string) => {
    if (!isValidTab(next)) return;
    setTab(next);
    // Functional updater + fresh URLSearchParams — never mutate the
    // object returned by useSearchParams.
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === "overview") {
          params.delete("tab");
        } else {
          params.set("tab", next);
        }
        return params;
      },
      { replace: true },
    );
  };

  const [compare, setCompare] = useState<CompareWith>("previous_month");
  const { displayCurrency } = useDisplayCurrency();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["group-reports"] });
  const isAnyFetching = useIsFetching({ queryKey: ["group-reports"] }) > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Hero ─────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0E2C44] text-white relative overflow-hidden">
        <div
          className="absolute -top-24 left-1/3 h-40 w-[40rem] rounded-full bg-[#4CAF50]/20 blur-3xl"
          aria-hidden
        />
        <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-8 relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <button
                type="button"
                onClick={() => navigate("/portfolio")}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#7BAEC8] hover:text-white transition-colors mb-1.5"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to portfolio
              </button>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#7BAEC8] mb-1">
                <Building2 className="h-3 w-3" />
                Group view
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold">
                Consolidated across all businesses
              </h1>
              <p className="text-sm text-[#A0BBCB] mt-1">
                Display currency converts at cached daily FX rates. Accounting
                records unchanged.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <RenderBoundary fallback={<span aria-hidden />}>
                <CurrencySwitcher />
              </RenderBoundary>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
              <label className="sr-only" htmlFor="group-compare">Compare with</label>
              <select
                id="group-compare"
                value={compare}
                onChange={(e) => setCompare(e.target.value as CompareWith)}
                className="rounded-md border border-white/15 bg-white/10 px-2 py-1 text-xs text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50] [&>option]:text-foreground"
              >
                <option value="previous_month">vs same days last month</option>
                <option value="previous_period">vs the period before</option>
                <option value="previous_year">vs same time last year</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                onClick={refresh}
                disabled={isAnyFetching}
                className="text-white/80 hover:text-white hover:bg-white/15"
              >
                <RefreshCcw
                  className={`h-3.5 w-3.5 mr-2 ${isAnyFetching ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Body ─────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 py-6 sm:px-6">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="mb-5 max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview" className="gap-2">
              <LayoutGrid className="h-3.5 w-3.5" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="pnl" className="gap-2">
              <FileBarChart2 className="h-3.5 w-3.5" />
              Profit &amp; Loss
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="gap-2">
              <Banknote className="h-3.5 w-3.5" />
              Cash Flow
            </TabsTrigger>
            <TabsTrigger value="trialbalance" className="gap-2">
              <Scale className="h-3.5 w-3.5" />
              Trial Balance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <div className="space-y-5">
              <GroupPerformance from={dateRange.from} to={dateRange.to} compare={compare} currency={displayCurrency} />
              <ReportLinks onJumpTo={handleTabChange} />
            </div>
          </TabsContent>

          <TabsContent value="pnl" className="mt-0">
            <GroupPnLDetail from={dateRange.from} to={dateRange.to} />
          </TabsContent>

          <TabsContent value="cashflow" className="mt-0">
            <GroupCashFlowDetail from={dateRange.from} to={dateRange.to} />
          </TabsContent>

          <TabsContent value="trialbalance" className="mt-0">
            <GroupTrialBalanceDetail asOf={dateRange.to || today()} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ── Links to the detailed statements ──────────────────────────────

const ReportLinks: React.FC<{ onJumpTo: (tab: string) => void }> = ({ onJumpTo }) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
    <BigReportButton onClick={() => onJumpTo("pnl")} icon={FileBarChart2} label="Profit & Loss" description="Revenue minus expenses, per business" />
    <BigReportButton onClick={() => onJumpTo("cashflow")} icon={Banknote} label="Cash Flow" description="Money in, money out, per business" />
    <BigReportButton onClick={() => onJumpTo("trialbalance")} icon={Scale} label="Trial Balance" description="Books balance — debits = credits" />
  </div>
);

const BigReportButton: React.FC<{
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  description: string;
}> = ({ onClick, icon: Icon, label, description }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex items-center gap-3 rounded-lg border bg-card hover:border-[#4CAF50]/40 hover:bg-[#4CAF50]/5 px-4 py-4 transition-colors w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4CAF50]"
  >
    <div className="h-10 w-10 rounded-md bg-[#4CAF50]/10 group-hover:bg-[#4CAF50]/20 flex items-center justify-center shrink-0 transition-colors">
      <Icon className="h-5 w-5 text-[#4CAF50]" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold truncate">Open {label}</div>
      <div className="text-[11px] text-muted-foreground truncate">
        {description}
      </div>
    </div>
  </button>
);

const DateRangePicker: React.FC<{
  value: { from: string; to: string };
  onChange: (v: { from: string; to: string }) => void;
}> = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5 bg-white/10 rounded-md px-2 py-1 text-xs text-white border border-white/15">
    <CalendarDays className="h-3.5 w-3.5 text-[#7BAEC8]" />
    <input
      type="date"
      value={value.from}
      max={value.to}
      onChange={(e) => onChange({ ...value, from: e.target.value })}
      className="bg-transparent text-white text-xs focus:outline-none [color-scheme:dark]"
    />
    <span className="text-white/40">→</span>
    <input
      type="date"
      value={value.to}
      min={value.from}
      max={today()}
      onChange={(e) => onChange({ ...value, to: e.target.value })}
      className="bg-transparent text-white text-xs focus:outline-none [color-scheme:dark]"
    />
  </div>
);

export default GroupView;
